import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import { useParams, useNavigate, Link } from '@tanstack/react-router';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AddIcon from '@mui/icons-material/Add';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import TimelineIcon from '@mui/icons-material/Timeline';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import PublicIcon from '@mui/icons-material/Public';
import {
  useProject,
  useDeleteProject,
  type BookSummaryDto,
  type OutlineSummaryDto,
} from '../api/queries/projects';
import {
  useCreateBook,
  useDuplicateBook,
  useSetPrimaryBook,
  useRenameBook,
  useDeleteBook,
} from '../api/queries/books';
import {
  useDuplicateOutline,
  useSetPrimaryOutline,
  useRenameOutline,
  useDeleteOutline,
} from '../api/queries/outlines';
import { useProjectStore } from '../stores/project.store';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { GenerateOutlineDialog } from '../components/outline/GenerateOutlineDialog';

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function stageColor(
  stage: string | null,
): 'default' | 'primary' | 'success' | 'warning' | 'info' {
  switch (stage?.toLowerCase()) {
    case 'draft':
      return 'default';
    case 'review':
      return 'info';
    case 'final':
      return 'success';
    case 'revised':
      return 'warning';
    default:
      return 'default';
  }
}

// --- Rename Dialog ---

function RenameDialog({
  open,
  currentLabel: initialLabel,
  onClose,
  onConfirm,
}: {
  open: boolean;
  currentLabel: string;
  onClose: () => void;
  onConfirm: (newLabel: string) => void;
}) {
  const [label, setLabel] = useState(initialLabel);

  const handleOpen = () => {
    setLabel(initialLabel);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      TransitionProps={{ onEnter: handleOpen }}
    >
      <DialogTitle>Rename</DialogTitle>
      <DialogContent>
        <TextField
          label="New Label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          fullWidth
          size="small"
          autoFocus
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => {
            onConfirm(label.trim());
            onClose();
          }}
          variant="contained"
          disabled={!label.trim()}
        >
          Rename
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// --- Outline Version Card ---

function OutlineVersionCard({
  outline,
  projectId,
}: {
  outline: OutlineSummaryDto;
  projectId: string;
}) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const setPrimary = useSetPrimaryOutline();
  const duplicateOutline = useDuplicateOutline();
  const renameOutline = useRenameOutline();
  const deleteOutlineMutation = useDeleteOutline();

  const handleMenuClose = () => setAnchorEl(null);

  return (
    <>
      <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {outline.isPrimary ? (
              <StarIcon fontSize="small" sx={{ color: 'warning.main' }} />
            ) : (
              <StarBorderIcon
                fontSize="small"
                sx={{ color: 'text.disabled' }}
              />
            )}
            <Typography
              variant="subtitle1"
              fontWeight={600}
              noWrap
              sx={{ flex: 1 }}
            >
              v{outline.versionNumber}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => setAnchorEl(e.currentTarget)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography variant="body2" color="text.secondary" noWrap>
            {outline.label}
          </Typography>

          {outline.sourceModel && (
            <Chip
              label={`AI (${outline.sourceModel})`}
              size="small"
              icon={<SmartToyIcon />}
              sx={{ alignSelf: 'flex-start' }}
            />
          )}

          <Typography variant="caption" color="text.secondary">
            {outline.nodeCount} node{outline.nodeCount !== 1 ? 's' : ''}
          </Typography>

          {outline.nodeCount === 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontStyle: 'italic' }}
            >
              Your outline is empty — add nodes or generate with AI.
            </Typography>
          )}

          {outline.stage && (
            <Chip
              label={outline.stage}
              size="small"
              color={stageColor(outline.stage)}
              sx={{ alignSelf: 'flex-start' }}
            />
          )}

          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                navigate({
                  to: '/outlines/$outlineId',
                  params: { outlineId: outline.id },
                })
              }
            >
              Open
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleMenuClose}>
        {!outline.isPrimary && (
          <MenuItem
            onClick={() => {
              setPrimary.mutate({ outlineId: outline.id, projectId });
              handleMenuClose();
            }}
          >
            Set as Primary
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            duplicateOutline.mutate({ outlineId: outline.id, projectId });
            handleMenuClose();
          }}
        >
          Duplicate
        </MenuItem>
        <MenuItem
          onClick={() => {
            setRenameOpen(true);
            handleMenuClose();
          }}
        >
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteOpen(true);
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>

      <RenameDialog
        open={renameOpen}
        currentLabel={outline.label}
        onClose={() => setRenameOpen(false)}
        onConfirm={(newLabel) =>
          renameOutline.mutate({
            outlineId: outline.id,
            projectId,
            label: newLabel,
          })
        }
      />

      <ConfirmDialog
        open={deleteOpen}
        title="Delete Outline"
        message={`Are you sure you want to delete "${outline.label}"? This action cannot be undone.`}
        onConfirm={() => {
          deleteOutlineMutation.mutate(outline.id);
          setDeleteOpen(false);
        }}
        onCancel={() => setDeleteOpen(false)}
      />
    </>
  );
}

// --- Book Draft Card ---

function BookDraftCard({
  book,
  projectId,
}: {
  book: BookSummaryDto;
  projectId: string;
}) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const setPrimary = useSetPrimaryBook();
  const duplicateBook = useDuplicateBook();
  const renameBook = useRenameBook();
  const deleteBookMutation = useDeleteBook();

  const handleMenuClose = () => setAnchorEl(null);

  return (
    <>
      <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {book.isPrimary ? (
              <StarIcon fontSize="small" sx={{ color: 'warning.main' }} />
            ) : (
              <StarBorderIcon
                fontSize="small"
                sx={{ color: 'text.disabled' }}
              />
            )}
            <Typography
              variant="subtitle1"
              fontWeight={600}
              noWrap
              sx={{ flex: 1 }}
            >
              v{book.versionNumber}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => setAnchorEl(e.currentTarget)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography variant="body2" color="text.secondary" noWrap>
            {book.label}
          </Typography>

          {book.stage && (
            <Chip
              label={book.stage}
              size="small"
              color={stageColor(book.stage)}
              sx={{ alignSelf: 'flex-start' }}
            />
          )}

          {book.sourceOutlineLabel && (
            <Typography variant="caption" color="text.secondary">
              from {book.sourceOutlineLabel}
            </Typography>
          )}

          <Typography variant="caption" color="text.secondary">
            {book.chapterCount} chapter{book.chapterCount !== 1 ? 's' : ''}
          </Typography>

          {book.chapterCount === 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontStyle: 'italic' }}
            >
              No chapters yet — start writing or generate from an outline.
            </Typography>
          )}

          <Typography variant="caption" color="text.secondary">
            Updated {formatRelativeTime(book.updatedAt)}
          </Typography>

          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                navigate({
                  to: '/books/$bookId',
                  params: { bookId: book.id },
                })
              }
            >
              Open
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleMenuClose}>
        {!book.isPrimary && (
          <MenuItem
            onClick={() => {
              setPrimary.mutate({ bookId: book.id, projectId });
              handleMenuClose();
            }}
          >
            Set as Primary
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            duplicateBook.mutate({ bookId: book.id, projectId });
            handleMenuClose();
          }}
        >
          Duplicate
        </MenuItem>
        <MenuItem
          onClick={() => {
            setRenameOpen(true);
            handleMenuClose();
          }}
        >
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteOpen(true);
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>

      <RenameDialog
        open={renameOpen}
        currentLabel={book.label}
        onClose={() => setRenameOpen(false)}
        onConfirm={(newLabel) =>
          renameBook.mutate({
            bookId: book.id,
            projectId,
            label: newLabel,
          })
        }
      />

      <ConfirmDialog
        open={deleteOpen}
        title="Delete Book Draft"
        message={`Are you sure you want to delete "${book.label}"? This action cannot be undone.`}
        onConfirm={() => {
          deleteBookMutation.mutate(book.id);
          setDeleteOpen(false);
        }}
        onCancel={() => setDeleteOpen(false)}
      />
    </>
  );
}

// --- Shared Resource Card ---

function SharedResourceCard({
  title,
  icon,
  subtitle,
  to,
  disabled,
}: {
  title: string;
  icon: React.ReactNode;
  subtitle: string;
  to?: string;
  disabled?: boolean;
}) {
  const navigate = useNavigate();
  return (
    <Card variant="outlined">
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 1,
          py: 3,
        }}
      >
        {icon}
        <Typography variant="subtitle2" fontWeight={600}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          disabled={disabled || !to}
          onClick={() => to && navigate({ to })}
        >
          Open
        </Button>
      </CardContent>
    </Card>
  );
}

// --- Main Dashboard Route ---

export default function ProjectDashboardRoute() {
  const { projectId } = useParams({ strict: false }) as {
    projectId: string;
  };
  const { data: project, isLoading } = useProject(projectId);
  const deleteProject = useDeleteProject();
  const navigate = useNavigate();
  const setActiveProject = useProjectStore((s) => s.setActiveProject);

  const [deleteProjectOpen, setDeleteProjectOpen] = useState(false);
  const [generateOutlineOpen, setGenerateOutlineOpen] = useState(false);
  const [newOutlineDialogOpen, setNewOutlineDialogOpen] = useState(false);
  const [newOutlineLabel, setNewOutlineLabel] = useState('');
  const [newBookDialogOpen, setNewBookDialogOpen] = useState(false);
  const [newBookLabel, setNewBookLabel] = useState('');

  const createBook = useCreateBook();

  if (isLoading || !project) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="text.secondary">
          {isLoading ? 'Loading project...' : 'Project not found'}
        </Typography>
      </Box>
    );
  }

  const handleDeleteProject = () => {
    deleteProject.mutate(projectId, {
      onSuccess: () => {
        setActiveProject(null);
        navigate({ to: '/projects' });
      },
    });
  };

  return (
    <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
          }}
        >
          <IconButton component={Link} to="/projects" size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight={700} sx={{ flex: 1 }}>
            {project.name}
          </Typography>
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteProjectOpen(true)}
          >
            Delete
          </Button>
        </Box>
        {project.description && (
          <Typography variant="body1" color="text.secondary" sx={{ ml: 6 }}>
            {project.description}
          </Typography>
        )}
        {project.status && (
          <Box sx={{ ml: 6, mt: 1 }}>
            <Chip label={project.status} size="small" />
          </Box>
        )}
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Outlines Section */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Outlines
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setNewOutlineLabel('');
                setNewOutlineDialogOpen(true);
              }}
            >
              New Outline
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SmartToyIcon />}
              onClick={() => setGenerateOutlineOpen(true)}
            >
              From AI
            </Button>
          </Box>
        </Box>

        {project.outlines.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No outlines yet. Generate one to plan your story structure.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {project.outlines.map((outline) => (
              <Grid key={outline.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <OutlineVersionCard outline={outline} projectId={projectId} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Book Drafts Section */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Book Drafts
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setNewBookLabel('');
                setNewBookDialogOpen(true);
              }}
            >
              New Draft
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SmartToyIcon />}
              onClick={() => setGenerateOutlineOpen(true)}
            >
              From AI
            </Button>
          </Box>
        </Box>

        {project.books.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No book drafts yet. Create a new draft or generate one from an
            outline.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {project.books.map((book) => (
              <Grid key={book.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <BookDraftCard book={book} projectId={projectId} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Shared Resources Section */}
      <Box>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          Shared Resources
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SharedResourceCard
              title="Story Studio"
              icon={
                <AutoStoriesIcon sx={{ fontSize: 36, color: 'primary.main' }} />
              }
              subtitle={
                project.sessionId ? 'Session available' : 'Not configured'
              }
              to={
                project.sessionId
                  ? `/story-studio/${project.sessionId}`
                  : undefined
              }
              disabled={!project.sessionId}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SharedResourceCard
              title="Story Architect"
              icon={
                <TimelineIcon sx={{ fontSize: 36, color: 'primary.main' }} />
              }
              subtitle={
                project.architectId ? 'Architect available' : 'Not configured'
              }
              to={
                project.architectId
                  ? `/story-architect/${project.architectId}`
                  : undefined
              }
              disabled={!project.architectId}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SharedResourceCard
              title="Creativity Board"
              icon={
                <ViewKanbanIcon sx={{ fontSize: 36, color: 'primary.main' }} />
              }
              subtitle={project.boardId ? 'Board available' : 'Not configured'}
              to={project.boardId ? `/boards/${project.boardId}` : undefined}
              disabled={!project.boardId}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SharedResourceCard
              title="Worlds & Entities"
              icon={<PublicIcon sx={{ fontSize: 36, color: 'primary.main' }} />}
              subtitle={`${project.worldCount} world${project.worldCount !== 1 ? 's' : ''} / ${project.entityCount} entit${project.entityCount !== 1 ? 'ies' : 'y'}`}
              to="/worlds"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Delete Project Confirmation */}
      <ConfirmDialog
        open={deleteProjectOpen}
        title="Delete Project"
        message={`Are you sure you want to delete "${project.name}"? All associated books, outlines, and resources will be removed.`}
        onConfirm={handleDeleteProject}
        onCancel={() => setDeleteProjectOpen(false)}
      />

      {/* Generate Outline Dialog (AI) */}
      <GenerateOutlineDialog
        open={generateOutlineOpen}
        projectId={projectId}
        onClose={() => setGenerateOutlineOpen(false)}
      />

      {/* New Outline Dialog (Manual / Duplicate) */}
      <Dialog
        open={newOutlineDialogOpen}
        onClose={() => setNewOutlineDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>New Outline</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Outline Label"
            value={newOutlineLabel}
            onChange={(e) => setNewOutlineLabel(e.target.value)}
            placeholder={`v${(project.outlines.length || 0) + 1}`}
            sx={{ mt: 1 }}
          />
          {project.outlines.length > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: 'block' }}
            >
              This creates an empty outline. To duplicate an existing one, use
              the actions menu (⋮) on an outline card.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOutlineDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!newOutlineLabel.trim()}
            onClick={() => {
              // TODO: Wire to a CreateOutline API when re-added for manual creation within a project
              setNewOutlineDialogOpen(false);
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Book Draft Dialog */}
      <Dialog
        open={newBookDialogOpen}
        onClose={() => setNewBookDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>New Book Draft</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Draft Label"
            value={newBookLabel}
            onChange={(e) => setNewBookLabel(e.target.value)}
            placeholder={`Draft ${(project.books.length || 0) + 1}`}
            sx={{ mt: 1 }}
          />
          {project.books.length > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: 'block' }}
            >
              This creates an empty book draft. To duplicate an existing one,
              use the actions menu (⋮) on a book card.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewBookDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!newBookLabel.trim()}
            onClick={() => {
              createBook.mutate(
                {
                  title: project.name,
                  pov: 'third_limited',
                  tense: 'past',
                  projectId,
                  label: newBookLabel.trim(),
                },
                {
                  onSuccess: (result) => {
                    setNewBookDialogOpen(false);
                    navigate({
                      to: '/books/$bookId',
                      params: { bookId: result.data.id },
                    });
                  },
                },
              );
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
